import React, { useState, useEffect, useCallback } from 'react';
import { Play, Pause, Square, Clock, Timer, User, Briefcase, Building } from 'lucide-react';
import { api } from '../../features/auth/authStore';
import { useTimer } from '../../contexts/TimerContext';
import toast from 'react-hot-toast';

// Types
interface Client {
  id: string;
  displayName: string;
  legalName?: string;
}

interface Task {
  id: string;
  title: string;
  clientId: string;
  status: string;
}

interface TimeEntry {
  id: string;
  taskId?: string;
  clientId?: string;
  description: string;
  startTime: Date;
  endTime?: Date;
  duration: number;
  billable: boolean;
  status: string;
  category?: string;
}

interface ActiveTimer {
  id: string;
  taskId?: string;
  clientId?: string;
  taskTitle?: string;
  description: string;
  startTime: Date;
  duration: number;
  billable: boolean;
  status: string;
  category?: string;
  mode?: 'client-adhoc' | 'client-task' | 'internal';
}

interface EnhancedTimeTrackerProps {
  userRole: string;
  userId: string;
  onTimeEntryCreated?: (entry: TimeEntry) => void;
  onTimerStarted?: (timer: ActiveTimer) => void;
  onTimerStopped?: (entry: TimeEntry) => void;
  className?: string;
}

type TrackingMode = 'client-adhoc' | 'client-task' | 'internal';

const INTERNAL_WORK_CATEGORIES = [
  'Administration',
  'Meetings (Internal)',
  'Training & Learning',
  'R&D / Innovation',
  'Internal Support',
  'Business Development',
  'HR / Recruitment',
  'Compliance & Audit',
  'Process Improvement',
  'CSR / Events',
  'Leave / Breaks'
];

export const EnhancedTimeTracker: React.FC<EnhancedTimeTrackerProps> = ({
  userRole,
  userId,
  onTimeEntryCreated,
  onTimerStarted,
  onTimerStopped,
  className = ''
}) => {
  const { activeTimer, elapsedTime, loading, startTimer: startTimerContext, stopTimer: stopTimerContext } = useTimer();
  
  // State for tracking mode and selections
  const [trackingMode, setTrackingMode] = useState<TrackingMode>('client-adhoc');
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedTask, setSelectedTask] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [description, setDescription] = useState('');
  
  // Data state
  const [clients, setClients] = useState<Client[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const response = await api.get('/clients/active');
      setClients(response.data.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  }, []);

  // Fetch tasks for selected client
  const fetchTasksForClient = useCallback(async (clientId: string) => {
    if (!clientId) {
      setTasks([]);
      return;
    }

    setLoadingTasks(true);
    try {
      // Fetch all tasks for the client and filter on frontend
      // Since the API doesn't support multiple status values, we'll get all tasks and filter
      const response = await api.get('/tasks', {
        params: {
          clientId: clientId,
          limit: 100 // Get more tasks to show all available options
        }
      });

      // Filter tasks to only show tasks that can be worked on
      const allTasks = response.data.data?.tasks || [];

      // Filter for tasks that are available for work (draft, in_progress)
      // Draft tasks can be started, in_progress tasks can be continued
      const filteredTasks = allTasks.filter((task: Task) =>
        task.status === 'draft' || task.status === 'in_progress'
      );

      setTasks(filteredTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoadingTasks(false);
    }
  }, []);

  // Load clients on component mount
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Load tasks when client is selected in task-based mode
  useEffect(() => {
    if (trackingMode === 'client-task' && selectedClient) {
      fetchTasksForClient(selectedClient);
    } else {
      setTasks([]);
      setSelectedTask('');
    }
  }, [trackingMode, selectedClient, fetchTasksForClient]);

  // Reset selections when mode changes
  useEffect(() => {
    setSelectedClient('');
    setSelectedTask('');
    setSelectedCategory('');
    setDescription('');
  }, [trackingMode]);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const canStartTimer = (): boolean => {
    switch (trackingMode) {
      case 'client-adhoc':
        return !!selectedClient && !!description.trim();
      case 'client-task':
        return !!selectedClient && !!selectedTask && !!description.trim();
      case 'internal':
        return !!selectedCategory && !!description.trim();
      default:
        return false;
    }
  };

  const startTimer = async () => {
    if (!canStartTimer()) {
      let message = 'Please complete all required fields';
      switch (trackingMode) {
        case 'client-adhoc':
          message = 'Please select a client and enter description';
          break;
        case 'client-task':
          message = 'Please select a client, task, and enter description';
          break;
        case 'internal':
          message = 'Please select a category and enter description';
          break;
      }
      toast.error(message);
      return;
    }

    try {
      let taskId: string | undefined;
      let clientId: string | undefined;

      switch (trackingMode) {
        case 'client-adhoc':
          clientId = selectedClient;
          break;
        case 'client-task':
          taskId = selectedTask;
          clientId = selectedClient;
          break;
        case 'internal':
          // For internal work, no taskId or clientId needed
          break;
      }

      // Start timer with user's description and proper clientId
      await startTimerContext(taskId, description, clientId);

      if (onTimerStarted && activeTimer) {
        onTimerStarted({
          ...activeTimer,
          mode: trackingMode,
          clientId,
          category: trackingMode === 'internal' ? selectedCategory : undefined
        });
      }

      toast.success('Timer started successfully');
    } catch (error: any) {
      console.error('Error starting timer:', error);
      toast.error('Failed to start timer');
    }
  };

  const stopTimer = async () => {
    if (!activeTimer) return;

    try {
      // Stop the timer - this creates the time entry automatically
      await stopTimerContext();

      // Toast message is already shown by the timer context

      if (onTimerStopped) {
        const stoppedEntry = {
          ...activeTimer,
          endTime: new Date(),
          duration: elapsedTime,
          description: description,
          category: selectedCategory,
          clientId: selectedClient,
          taskId: selectedTask,
          mode: trackingMode
        };
        onTimerStopped(stoppedEntry);
      }

      // Reset form
      resetForm();
    } catch (error: any) {
      console.error('Error stopping timer:', error);
      // Error toast is already shown by the timer context
    }
  };



  const createCompleteTimeEntry = async (workDescription: string) => {
    try {
      let tags: string[] = [];
      let clientId: string | undefined;
      let taskId: string | undefined;
      let billable = true;

      switch (trackingMode) {
        case 'client-adhoc':
          clientId = selectedClient;
          tags = ['client-consultation', 'ad-hoc'];
          billable = true;
          break;
        case 'client-task':
          taskId = selectedTask;
          clientId = selectedClient;
          tags = ['client-task', 'task-based'];
          billable = true;
          break;
        case 'internal':
          tags = ['internal-work', selectedCategory.toLowerCase().replace(/[^a-z0-9]/g, '-')];
          billable = false; // Internal work is typically non-billable
          break;
      }

      // Create a complete time entry with proper client/task association
      const response = await api.post('/time-entries', {
        taskId: taskId || undefined,
        clientId: clientId || undefined,
        description: workDescription,
        startTime: activeTimer?.startTime,
        endTime: new Date(),
        duration: elapsedTime,
        billable,
        tags
      });

      return response.data.data;
    } catch (error) {
      console.error('Error creating complete time entry:', error);
      // Don't throw error here as the main timer stop should still work
      return {};
    }
  };

  const deleteBasicTimeEntry = async (timeEntryId: string) => {
    try {
      await api.delete(`/time-entries/${timeEntryId}`);
    } catch (error) {
      console.error('Error deleting basic time entry:', error);
      // Don't throw error here as the main functionality should still work
    }
  };

  const resetForm = () => {
    setSelectedClient('');
    setSelectedTask('');
    setSelectedCategory('');
    setDescription('');
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Timer className="w-5 h-5 mr-2" />
          Time Tracker
        </h3>
      </div>

      {/* Active Timer Display */}
      {activeTimer && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mr-3"></div>
              <div>
                <div className="font-mono text-2xl font-bold text-blue-900">
                  {formatDuration(elapsedTime)}
                </div>
                <div className="text-sm text-blue-700">
                  {activeTimer.description}
                </div>
              </div>
            </div>
            <button
              onClick={stopTimer}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              <Square className="w-4 h-4 mr-2" />
              Stop
            </button>
          </div>
        </div>
      )}

      {/* Timer Setup Form */}
      {!activeTimer && (
        <div className="space-y-6">
          {/* Mode Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Tracking Mode
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => setTrackingMode('client-adhoc')}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  trackingMode === 'client-adhoc'
                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <User className="w-5 h-5 mb-2" />
                <div className="font-medium">Client Consultation</div>
                <div className="text-sm text-gray-600">(Ad-hoc)</div>
              </button>

              <button
                onClick={() => setTrackingMode('client-task')}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  trackingMode === 'client-task'
                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Briefcase className="w-5 h-5 mb-2" />
                <div className="font-medium">Client Task</div>
                <div className="text-sm text-gray-600">Based</div>
              </button>

              <button
                onClick={() => setTrackingMode('internal')}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  trackingMode === 'internal'
                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Building className="w-5 h-5 mb-2" />
                <div className="font-medium">Internal Work</div>
                <div className="text-sm text-gray-600">Categories</div>
              </button>
            </div>
          </div>

          {/* Client Selection (for both client modes) */}
          {(trackingMode === 'client-adhoc' || trackingMode === 'client-task') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Client *
              </label>
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                disabled={loadingClients}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
              >
                <option value="">
                  {loadingClients ? 'Loading clients...' : 'Select a client'}
                </option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Task Selection (for client-task mode) */}
          {trackingMode === 'client-task' && selectedClient && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Task *
              </label>
              <select
                value={selectedTask}
                onChange={(e) => setSelectedTask(e.target.value)}
                disabled={loadingTasks}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
              >
                <option value="">
                  {loadingTasks ? 'Loading tasks...' : 'Select a task'}
                </option>
                {tasks.map(task => (
                  <option key={task.id} value={task.id}>
                    {task.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Description Field (for all modes) */}
          {(trackingMode === 'client-adhoc' || trackingMode === 'client-task') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={
                  trackingMode === 'client-adhoc'
                    ? "Describe the consultation work to be done..."
                    : "Describe the task work to be done..."
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          {/* Internal Work Category Selection */}
          {trackingMode === 'internal' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Category *
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a category</option>
                  {INTERNAL_WORK_CATEGORIES.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the work to be done..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </>
          )}

          {/* Start Timer Button */}
          <div className="flex justify-center">
            <button
              onClick={startTimer}
              disabled={loading || !canStartTimer()}
              className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play className="w-5 h-5 mr-2" />
              Start Timer
            </button>
          </div>
        </div>
      )}


    </div>
  );
};
